import * as React from 'react';
import {
  Box,
  Button,
  focusVisibleOverrideOuter,
  HeadingText,
  ParagraphText,
  SkeletonRectangle,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

const SKELETON_MESSAGE_COUNT = 5;

const MessageSkeleton = () => (
  <Box
    css={{
      display: 'flex',
      flexDirection: 'column',
      gap: '$half',
      width: '100%',
    }}
  >
    <SkeletonRectangle
      height="$one"
      width="30%"
      aspectRatio="auto"
      borderRadius="$small"
    />
    <SkeletonRectangle
      height="$one"
      width="100%"
      aspectRatio="auto"
      borderRadius="$small"
    />
    <SkeletonRectangle
      height="$one"
      width="80%"
      aspectRatio="auto"
      borderRadius="$small"
    />
  </Box>
);

export type ChatHistoryContainerProps = {
  title: string;
  subtitle: string;
  showBackButton?: boolean;
  isLoading?: boolean;
  onBackClick?: () => void;
  children?: React.ReactNode;
};

export const ChatHistoryContainer = React.forwardRef<
  HTMLDivElement,
  ChatHistoryContainerProps
>(
  (
    { title, subtitle, showBackButton, isLoading, onBackClick, children },
    ref,
  ) => {
    const { formatMessage } = useIntl();
    const isBackButtonVisible = showBackButton && !isLoading;
    return (
      <Box
        as="section"
        ref={ref}
        tabIndex={-1}
        aria-label={formatMessage({ id: 'CHAT_HISTORY_DETAILS' })}
        css={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '$surfaceBackgroundPrimary',
          borderRadius: '$large',
          border: '$borderWidths$thin solid $onSurfaceBorderSubdued',
          overflow: 'hidden',
          '@mobile': {
            borderRadius: 0,
            border: 'none',
            overflow: 'visible',
          },
          '@mobileLandscape': {
            borderRadius: 0,
            border: 'none',
            overflow: 'visible',
          },
          '@tablet': {
            borderRadius: 0,
            border: 'none',
            overflow: 'visible',
          },
          ...focusVisibleOverrideOuter,
        }}
      >
        <Box
          css={{
            paddingBlock: '$one',
            paddingInline: '$oneAndHalf',
            backgroundColor: '$surfaceBackgroundSecondary',
            flexShrink: 0,
          }}
        >
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '$threeQuarters',
              opacity: isLoading ? 0 : 1,
              pointerEvents: isLoading ? 'none' : 'auto',
            }}
            // Use the height of visible content to prevent layout shift when loading state changes
            // Do not expose content to screen readers when in loading state to avoid confusion
            aria-hidden={isLoading}
          >
            {isBackButtonVisible && (
              <Button
                icon="interfaceArrowLeft"
                hideLabel
                priority="tertiary"
                quiet
                size="small"
                onClick={onBackClick}
              >
                {formatMessage({ id: 'BACK' })}
              </Button>
            )}
            <Box>
              <HeadingText level="2" size="lg">
                {title}
              </HeadingText>
              <ParagraphText
                css={{ typography: '$caption', color: '$onSurfaceTextSubdued' }}
              >
                {subtitle}
              </ParagraphText>
            </Box>
          </Box>
        </Box>
        {isLoading ? (
          <StackLayout
            spacing="$oneAndHalf"
            css={{
              flex: 1,
              paddingBlock: '$one',
              paddingInline: '$oneAndHalf',
            }}
          >
            {Array.from({ length: SKELETON_MESSAGE_COUNT }, (_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <MessageSkeleton key={i} />
            ))}
          </StackLayout>
        ) : (
          children && (
            <StackLayout
              role="list"
              as="ul"
              spacing="$oneAndHalf"
              css={{
                flex: 1,
                paddingBlock: '$one',
                paddingInline: '$oneAndHalf',
              }}
            >
              {React.Children.map(children, (child, index) => (
                <Box
                  key={React.isValidElement(child) ? child.key : index}
                  as="li"
                  css={{ listStyle: 'none', width: '100%' }}
                >
                  {child}
                </Box>
              ))}
            </StackLayout>
          )
        )}
      </Box>
    );
  },
);
